import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  SquaresFour, UsersThree, Gear,
  Lightning, ChatCircleDots, CalendarCheck, Shield,
  BookOpen, ShieldCheck, PlugsConnected, ChartLineUp, ChatTeardropText,
  Pulse, MegaphoneSimple, CaretRight, Megaphone,
  FlagCheckered, IdentificationBadge, Star, Sparkle, Question, Lock,
  ClockCounterClockwise, Code, Brain, Buildings, ListChecks,
} from "@phosphor-icons/react";
import SidebarBillingCard from "@/components/SidebarBillingCard";
import { useAuth } from "@/contexts/AuthContext";
import { useLiveCounts } from "@/contexts/LiveCountsContext";
import { canAccess } from "@/lib/permissions";
import { useSector } from "@/lib/sectorContext";

/**
 * Sidebar — 5 pilares AgentUS.
 *
 * v0.99.11 · Fev/2026 — Reorganização por "pilares" segundo o modelo
 * mental de dono de estabelecimento:
 *   1. Cérebro       — o que a IA sabe (identidade, vozes, serviços…)
 *   2. Automações    — quando a IA age (gatilhos)
 *   3. Conversas     — onde a IA opera (WhatsApp + histórico + agenda)
 *   4. Impacto       — o que a IA rendeu
 *   5. Configuração  — configurar uma vez, esquecer
 *
 * Cérebro e Automações são pilares mono-item, portanto ficam expostos
 * como NavLinks "featured" (não colapsáveis) — o resto vira grupo.
 */

// ── Standalone links no topo ────────────────────────────────────────
const DASHBOARD  = { to: "/app",          label: "Dashboard",  icon: SquaresFour, end: true, area: "dashboard" };
// v0.99.11 — Renomeado "Operação" → "Cérebro". Rota /app/operacao
// preservada para não quebrar links, bookmarks e onboarding e-mails.
const CEREBRO    = { to: "/app/operacao", label: "Cérebro",    icon: Brain,       area: "identity",  featured: true };
const AUTOMACOES = { to: "/app/decisao",  label: "Automações", icon: Pulse,       area: "decisions", featured: true };

// ── Grupos (pilares multi-item) ─────────────────────────────────────
const GROUPS = [
  // 💬 CONVERSAS — onde a IA opera. É o dia-a-dia do dono.
  {
    id: "conversas",
    label: "Conversas",
    defaultOpen: true,
    items: [
      { to: "/app/conversas",           label: "Conversas",         icon: ChatCircleDots, area: "conversations" },
      { to: "/app/perguntas-pendentes", label: "Perguntas Pendentes", icon: Question,     area: "conversations" },
      { to: "/app/agendamentos",        label: "Agenda",            icon: CalendarCheck, area: "appointments" },
      // Nome do item vira dinâmico via sector context (ver visibleGroups).
      { to: "/app/clientes",            label: "Clientes e Veículos", icon: UsersThree,   area: "customers" },
    ],
  },

  // 📈 IMPACTO — o que a IA rendeu. Ex-"ROI por regra".
  {
    id: "impacto",
    label: "Impacto",
    defaultOpen: false,
    items: [
      // v0.99.11 — Rota /app/roi mantida (rebrand só de label).
      { to: "/app/roi",         label: "Impacto do AgentUS", icon: ChartLineUp,      area: "reports" },
      { to: "/app/campanhas",   label: "Campanhas",          icon: MegaphoneSimple,  area: "messages_hub" },
      { to: "/app/avaliacoes",  label: "Avaliações",         icon: Star,             area: "reports" },
    ],
  },

  // ⚙️ CONFIGURAÇÃO — configurar uma vez, esquecer.
  {
    id: "setup",
    label: "Configuração",
    defaultOpen: false,
    items: [
      { to: "/app/integracoes",   label: "Integrações",         icon: PlugsConnected,    area: "integrations" },
      { to: "/app/inteligencia",  label: "Segmentos",           icon: IdentificationBadge, area: "knowledge" },
      { to: "/app/modo-teste",    label: "Modo Teste",          icon: ShieldCheck,       area: "identity" },
      { to: "/app/acoes",         label: "Permissões da IA",    icon: Shield,            area: "decisions" },
      { to: "/app/privacidade",   label: "Privacidade (LGPD)",  icon: Lock,              area: "identity" },
      { to: "/app/eventos",       label: "Eventos",             icon: Lightning,         area: "reports" },
      { to: "/app/auditoria",     label: "Auditoria",           icon: ClockCounterClockwise, area: "audit" },
      { to: "/app/desenvolvedor", label: "Desenvolvedor",       icon: Code,              area: "integrations" },
    ],
  },
];

// v0.99.11 — bump da storage key porque a estrutura de grupos mudou.
// Sem isso, tenants antigos ficariam com estado colapsado de grupos
// que não existem mais (ex: "dia-a-dia") e não veriam os novos.
const STORAGE_KEY = "agentos_sidebar_groups_v17";

function loadOpenState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveOpenState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function testidFromLabel(label) {
  return label
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[()]/g, "")
    .replace(/\s+/g, "-")
    .replace(/&/g, "and");
}

export default function Sidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const { labels, enablesAssetTab, enablesKm, isMandate } = useSector();

  // Filtro de acesso + label dinâmico dos clientes (por setor).
  const visibleGroups = useMemo(() => {
    if (!user) return [];
    const customersLabel = enablesAssetTab
      ? `Clientes e ${labels.assetPlural(true)}`
      : labels.assetPlural(true);
    // v0.99.46 — grupo exclusivo "Mandato" aparece só quando sector=mandate.
    const extraGroups = isMandate ? [{
      id: "mandato",
      label: "Mandato",
      defaultOpen: true,
      items: [
        { to: "/app/mandato/representantes", label: "Representantes", icon: Buildings, area: "customers" },
        { to: "/app/mandato/demandas",       label: "Demandas",       icon: ListChecks, area: "customers" },
        { to: "/app/mandato/agenda",         label: "Agenda & Contas", icon: FlagCheckered, area: "customers" },
      ],
    }] : [];
    return [...extraGroups, ...GROUPS]
      .map((g) => {
        const items = g.items
          .filter((it) => !it.area || canAccess(user, it.area))
          .filter((it) => {
            if (it.to === "/app/veiculos" && !enablesKm) return false;
            return true;
          })
          .map((it) =>
            it.to === "/app/clientes" ? { ...it, label: customersLabel } : it,
          );
        return { ...g, items };
      })
      .filter((g) => g.items.length > 0);
  }, [user, enablesAssetTab, enablesKm, labels, isMandate]);

  const activeGroupId = useMemo(() => {
    for (const g of visibleGroups) {
      if (g.items.some((it) => location.pathname.startsWith(it.to))) return g.id;
    }
    return null;
  }, [location.pathname, visibleGroups]);

  const [open, setOpen] = useState(() => {
    const saved = loadOpenState();
    const base = Object.fromEntries(GROUPS.map((g) => [g.id, g.defaultOpen]));
    return saved ? { ...base, ...saved } : base;
  });

  const { pendingQuestionsCount, awaitingHumanCount, negativeReviewsCount } = useLiveCounts();
  useEffect(() => {
    const hasUrgent = (pendingQuestionsCount || 0) + (awaitingHumanCount || 0) + (negativeReviewsCount || 0) > 0;
    if (hasUrgent) {
      setOpen((prev) => prev["conversas"] ? prev : { ...prev, "conversas": true });
    }
  }, [pendingQuestionsCount, awaitingHumanCount, negativeReviewsCount]);

  useEffect(() => {
    if (activeGroupId && !open[activeGroupId]) {
      setOpen((prev) => ({ ...prev, [activeGroupId]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupId]);

  useEffect(() => {
    saveOpenState(open);
  }, [open]);

  const toggle = (id) => setOpen((prev) => ({ ...prev, [id]: !prev[id] }));

  // Filtra Cérebro/Automações por permissão do usuário — mesma regra dos grupos.
  const cerebroVisible    = !user ? false : (!CEREBRO.area    || canAccess(user, CEREBRO.area));
  const automacoesVisible = !user ? false : (!AUTOMACOES.area || canAccess(user, AUTOMACOES.area));

  return (
    <aside
      className="w-64 shrink-0 h-screen sticky top-0 border-r border-slate-200 bg-slate-50 flex flex-col"
      data-testid="app-sidebar"
    >
      <div className="h-20 px-5 flex items-center border-b border-slate-200">
        <img
          src="/logo-black.png"
          alt="AgentUS"
          className="h-[18px] w-auto select-none"
          data-testid="sidebar-logo"
          draggable="false"
        />
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1 sidebar-scroll">
        {/* Pilares standalone: Dashboard + Cérebro + Automações */}
        <NavLinkRow item={DASHBOARD} />
        {cerebroVisible && <NavLinkRow item={CEREBRO} />}
        {automacoesVisible && <NavLinkRow item={AUTOMACOES} />}

        {/* Pilares agrupados: Conversas, Impacto, Configuração */}
        <div className="pt-2 space-y-3">
          {visibleGroups.map((g) => {
            const isOpen = open[g.id] === undefined ? !!g.defaultOpen : !!open[g.id];
            const hasActive = activeGroupId === g.id;
            return (
              <div key={g.id}>
                <button
                  type="button"
                  onClick={() => toggle(g.id)}
                  data-testid={`sidebar-group-${g.id}`}
                  className="sidebar-group-header w-full flex items-center justify-between px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-slate-400 hover:text-slate-700 transition-all duration-200 group"
                  aria-expanded={isOpen}
                >
                  <span className={`font-medium transition-transform duration-200 ease-out group-hover:translate-x-[1px] ${hasActive ? "text-slate-700" : ""}`}>
                    {g.label}
                  </span>
                  <CaretRight
                    size={10}
                    weight="bold"
                    className={`transition-all duration-200 ease-out ${isOpen ? "rotate-90 text-slate-500" : "text-slate-300"} group-hover:text-slate-600`}
                  />
                </button>

                <div
                  className="overflow-hidden transition-all duration-200 ease-out"
                  style={{
                    maxHeight: isOpen ? `${g.items.length * 36 + 8}px` : "0px",
                    opacity: isOpen ? 1 : 0,
                  }}
                >
                  <div className="pt-1 space-y-0.5">
                    {g.items.map((item) => (
                      <NavLinkRow key={item.to} item={item} />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </nav>

      <div className="p-3 border-t border-slate-200" data-testid="sidebar-footer">
        <SidebarBillingCard />
      </div>
    </aside>
  );
}

function NavLinkRow({ item }) {
  const live = useLiveCounts();
  let badge = null;
  if (item.to === "/app/conversas" && live.awaitingHumanCount > 0) {
    badge = {
      n: live.awaitingHumanCount,
      tone: "amber",
      title: `${live.awaitingHumanCount} aguardando atendimento humano`,
    };
  } else if (item.to === "/app/perguntas-pendentes" && live.pendingQuestionsCount > 0) {
    badge = {
      n: live.pendingQuestionsCount,
      tone: "amber",
      title: `${live.pendingQuestionsCount} pergunta${live.pendingQuestionsCount === 1 ? "" : "s"} aguardando você alimentar a IA`,
    };
  } else if (item.to === "/app/avaliacoes" && live.negativeReviewsCount > 0) {
    badge = {
      n: live.negativeReviewsCount,
      tone: "rose",
      title: `${live.negativeReviewsCount} avaliação${live.negativeReviewsCount === 1 ? "" : "ões"} 1★/2★ — resgate o cliente`,
    };
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={`nav-${testidFromLabel(item.label)}`}
      className={({ isActive }) =>
        `sidebar-nav-row group flex items-center gap-3 px-3 py-1.5 rounded-md text-[13px] no-underline transition-all duration-200 ease-out relative ${
          isActive
            ? "bg-slate-100 text-slate-900 font-medium"
            : item.featured
            ? "text-slate-900 font-medium hover:bg-slate-100/60"
            : "text-slate-500 hover:text-slate-900 hover:bg-slate-100/60"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] bg-slate-900 rounded-r" />
          )}
          <item.icon
            size={16}
            weight={item.featured ? "fill" : "regular"}
            className={`shrink-0 transition-transform duration-200 ease-out group-hover:scale-110 ${
              item.featured && !isActive ? "text-slate-900" : ""
            }`}
          />
          <span className="tracking-tight transition-transform duration-200 ease-out group-hover:translate-x-[2px] flex-1">
            {item.label}
          </span>
          {badge && (
            <span
              data-testid={`nav-badge-${testidFromLabel(item.label)}`}
              title={badge.title}
              className={`shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold tabular-nums leading-none ${
                badge.tone === "rose"
                  ? "bg-rose-600 text-white"
                  : "bg-amber-500 text-white"
              }`}
            >
              {badge.n > 99 ? "99+" : badge.n}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
